import { FunctionComponent } from 'preact';
import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { Asset } from '@schibsted-svp/svp-api-types';

import { useSchibstedAccount } from '../../../../utils/SchibstedAccountProvider';
import { PortalOverlay } from '../PortalOverlay/PortalOverlay';
import { BaseButton } from '../BaseButton/BaseButton';
import { accessLevelToSpsSection } from '../../services/sps/spsApi';
import { useApis } from '../../hooks/ApisProvider';
import { resolveWithTimeout } from '../../../../utils/async';
import { useIcons } from '../../hooks/useIcons';

const IFRAME_ID = 'salesposter_iframe';

interface SalesPosterProps {
  asset: Asset;
  accessLevel: 'plus' | 'sport' | 'total';
  isOpen: boolean;
  onCloseClick: () => void;
  onLoadFailure: () => void;
}

export const SalesPoster: FunctionComponent<SalesPosterProps> = ({
  asset,
  accessLevel,
  isOpen,
  onCloseClick,
  onLoadFailure,
}) => {
  const [iframeContent, setIframeContent] = useState<string>();
  const [isIframeRendered, setIsIframeRendered] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [iframeId, setIframeId] = useState<string>(IFRAME_ID);
  const { Cancel } = useIcons();

  const { spsApi } = useApis();

  const { clearSessionCache, clearAccessCache, getIdentity } =
    useSchibstedAccount();

  const iframeRef = useRef<HTMLIFrameElement>(null!);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseClick();
      }
    };

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [onCloseClick]);

  useEffect(() => {
    (async () => {
      try {
        const identity = await resolveWithTimeout({
          promise: getIdentity(),
          fallback: null,
          timeout: 1500,
        });

        if (!identity) {
          onLoadFailure();

          return;
        }

        const { html, iframeId: posterIframeId } = await spsApi.getSalesposter({
          section: accessLevelToSpsSection(accessLevel),
          placement: 'subscription',
          userId: (await identity.isLoggedIn())
            ? await identity.getUserId()
            : null,
          refId: asset.id,
          entityId: asset.id,
          redirectUri: identity.redirectUri,
          getLoginUrl: identity.loginUrl.bind(identity),
        });

        setIframeContent(html);
        setIframeId(posterIframeId ?? IFRAME_ID);
      } catch (err) {
        console.error(err);

        onLoadFailure();
      }
    })();
  }, [getIdentity, accessLevel, asset, onLoadFailure, spsApi]);

  useEffect(() => {
    const iframeDoc = iframeRef.current.contentDocument;

    if (!iframeDoc || !iframeContent) {
      return;
    }

    setIsIframeRendered(true);

    const timeoutId = setTimeout(() => setIsIframeLoaded(true), 500);

    iframeDoc.open();
    iframeDoc.writeln(iframeContent);
    iframeDoc.close();

    return () => clearTimeout(timeoutId);
  }, [iframeContent]);

  useEffect(() => {
    if (isOpen) {
      clearAccessCache().then(() => {
        clearSessionCache();
      });
    }
  }, [isOpen, clearSessionCache, clearAccessCache]);

  const onIframeLoad = useCallback(() => {
    // the load event is also triggered when an empty iframe is first rendered,
    // so we need to check whether the content has actually been injected
    if (isIframeRendered) {
      setIsIframeLoaded(true);
    }
  }, [isIframeRendered]);

  return (
    <PortalOverlay isOpen={isOpen}>
      <BaseButton onClick={onCloseClick} className="vgp-sales-poster-button">
        <Cancel />
      </BaseButton>
      <iframe
        ref={iframeRef}
        id={iframeId}
        frameBorder="0"
        scrolling="no"
        className={cn('vgp-sales-poster', {
          'vgp-sales-poster-loaded': isIframeLoaded,
        })}
        // the load event will trigger when all iframe resources are fully loaded
        // - this way no rendering glitches are visible to the user while styles / images are loading
        onLoad={onIframeLoad}
      />
    </PortalOverlay>
  );
};
